import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import type { Table, TableForm } from '~/types/tables'

export const useTablesStore = defineStore('tables', () => {
  const log = useLogger('store:tables')
  const { get, post, put, delete: del } = useAuthenticatedFetch()
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)

  // State
  const tables: Ref<Table[]> = ref([])
  const loading: Ref<boolean> = ref(false)

  // Filter states
  const searchTerm: Ref<string> = ref('')
  const showInactive: Ref<boolean> = ref(false)
  const filterDiningAreaId: Ref<number | undefined> = ref(undefined)
  const filterStatus: Ref<'available' | 'occupied' | 'reserved' | 'cleaning' | undefined> = ref(undefined)

  // UI states
  const isCollapsed: Ref<boolean> = ref(false)

  const nuxtApp = useNuxtApp()
  const { t } = nuxtApp.$i18n
  const { $confirm } = nuxtApp

  // Core data fetching
  async function fetchTables(): Promise<void> {
    if (!businessId.value) return

    loading.value = true
    try {
      const params = new URLSearchParams()
      if (showInactive.value) params.append('include_inactive', 'true')
      if (filterDiningAreaId.value) params.append('dining_area_id', filterDiningAreaId.value.toString())
      if (filterStatus.value) params.append('status', filterStatus.value)

      const queryString = params.toString()
      const url = `/api/business/${businessId.value}/tables${queryString ? `?${queryString}` : ''}`

      const res = await get(url)
      tables.value = res.data || []
    }
    catch (error: any) {
      log.error('Error fetching tables:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('App is offline - using cached tables')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to load tables')
      }
      else {
        useErrorToast('Failed to load tables')
      }
    }
    finally {
      loading.value = false
    }
  }

  async function fetchTableById(tableId: number): Promise<Table | null> {
    if (!businessId.value) return null

    try {
      const res = await get(`/api/business/${businessId.value}/tables/${tableId}`)
      return res.data || null
    }
    catch (error: any) {
      log.error('Error fetching table:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('App is offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in')
      }
      else {
        useErrorToast('Failed to load table')
      }
      return null
    }
  }

  // CRUD operations
  async function createTable(formData: TableForm): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await post(`/api/business/${businessId.value}/tables`, formData)

      if (res.success) {
        useSuccessToast('Table created successfully')
        await fetchTables()
        return true
      }
      else {
        const errorMsg = res.message ? `Failed to create table: ${res.message}` : 'Failed to create table'
        useErrorToast(errorMsg)
        return false
      }
    }
    catch (error: any) {
      log.error('Error creating table:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot create table while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to create tables')
      }
      else {
        const errorMessage = error?.data?.message || error?.message || 'Unknown error'
        useErrorToast(`Failed to create table: ${errorMessage}`)
      }
      return false
    }
  }

  async function updateTable(tableId: number, formData: Partial<TableForm>): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await put(`/api/business/${businessId.value}/tables/${tableId}`, formData)

      if (res.success) {
        useSuccessToast('Table updated successfully')
        await fetchTables()
        return true
      }
      else {
        const errorMsg = res.message ? `Failed to update table: ${res.message}` : 'Failed to update table'
        useErrorToast(errorMsg)
        return false
      }
    }
    catch (error: any) {
      log.error('Error updating table:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot update table while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to update tables')
      }
      else {
        const errorMessage = error?.data?.message || error?.message || 'Unknown error'
        useErrorToast(`Failed to update table: ${errorMessage}`)
      }
      return false
    }
  }

  async function updateTableStatus(tableId: number, status: 'available' | 'occupied' | 'reserved' | 'cleaning'): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await put(`/api/business/${businessId.value}/tables/${tableId}/status`, { status })

      if (res.success) {
        // Toast removed - used by POS internally when opening checks, no need for user notification
        // useSuccessToast('Table status updated successfully')
        await fetchTables()
        return true
      }
      else {
        useErrorToast('Failed to update table status')
        return false
      }
    }
    catch (error: any) {
      log.error('Error updating table status:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot update table status while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to update table status')
      }
      else {
        useErrorToast('Failed to update table status')
      }
      return false
    }
  }

  async function deleteTable(tableId: number, hardDelete = false): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const queryParam = hardDelete ? '?hard=true' : ''
      const res = await del(`/api/business/${businessId.value}/tables/${tableId}${queryParam}`)

      if (res.success) {
        useSuccessToast(hardDelete ? 'Table deleted successfully' : 'Table deactivated successfully')
        await fetchTables()
        return true
      }
      else {
        useErrorToast('Failed to delete table')
        return false
      }
    }
    catch (error: any) {
      log.error('Error deleting table:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot delete table while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to delete tables')
      }
      else {
        useErrorToast('Failed to delete table')
      }
      return false
    }
  }

  async function activateTable(tableId: number): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await put(`/api/business/${businessId.value}/tables/${tableId}`, { active: true })

      if (res.success) {
        useSuccessToast('Table activated successfully')
        await fetchTables()
        return true
      }
      else {
        useErrorToast('Failed to activate table')
        return false
      }
    }
    catch (error: any) {
      log.error('Error activating table:', error)
      useErrorToast('Failed to activate table')
      return false
    }
  }

  async function deactivateTableWithConfirm(tableId: number): Promise<void> {
    $confirm.require({
      group: 'inline',
      message: t('Do you want to deactivate this table?'),
      icon: 'pi pi-info-circle',
      rejectProps: {
        label: t('Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: t('Deactivate'),
        severity: 'warning',
      },
      accept: async () => {
        await deleteTable(tableId, false)
      },
      reject: () => {
        // User cancelled
      },
    })
  }

  async function deleteTableWithConfirm(tableId: number): Promise<void> {
    $confirm.require({
      group: 'inline',
      message: t('Do you want to permanently delete this table?'),
      icon: 'pi pi-exclamation-triangle',
      rejectProps: {
        label: t('Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: t('Delete'),
        severity: 'danger',
      },
      accept: async () => {
        await deleteTable(tableId, true)
      },
      reject: () => {
        // User cancelled
      },
    })
  }

  // Computed properties for filtering
  const filteredTables = computed(() => {
    let filtered = tables.value

    // Filter by search term
    if (searchTerm.value.trim()) {
      const term = searchTerm.value.toLowerCase()
      filtered = filtered.filter(table =>
        table.table_number.toLowerCase().includes(term)
        || table.dining_area?.name?.toLowerCase().includes(term),
      )
    }

    // Filter by active status
    if (!showInactive.value) {
      filtered = filtered.filter(table => table.active)
    }

    return filtered
  })

  const totalTables = computed(() => tables.value.length)

  const activeTables = computed(() =>
    tables.value.filter(table => table.active).length,
  )

  const inactiveTables = computed(() =>
    tables.value.filter(table => !table.active).length,
  )

  const availableTables = computed(() =>
    tables.value.filter(table => table.active && table.status === 'available').length,
  )

  const occupiedTables = computed(() =>
    tables.value.filter(table => table.active && table.status === 'occupied').length,
  )

  // FormKit select options
  const tableOptions = computed(() =>
    tables.value
      .filter(table => table.active)
      .map(table => ({
        label: table.dining_area ? `${table.table_number} - ${table.dining_area.name}` : table.table_number,
        value: table.id,
      })),
  )

  // Utility functions
  function getTableById(id: number): Table | undefined {
    return tables.value.find(table => table.id === id)
  }

  function getTableByNumber(tableNumber: string): Table | undefined {
    return tables.value.find(table => table.table_number === tableNumber)
  }

  // Search and filter methods
  function setSearchTerm(term: string): void {
    searchTerm.value = term
  }

  function toggleShowInactive(): void {
    showInactive.value = !showInactive.value
    fetchTables()
  }

  function setDiningAreaFilter(diningAreaId: number | undefined): void {
    filterDiningAreaId.value = diningAreaId
    fetchTables()
  }

  function setStatusFilter(status: 'available' | 'occupied' | 'reserved' | 'cleaning' | undefined): void {
    filterStatus.value = status
    fetchTables()
  }

  function clearFilters(): void {
    searchTerm.value = ''
    showInactive.value = false
    filterDiningAreaId.value = undefined
    filterStatus.value = undefined
    fetchTables()
  }

  function toggleCollapsed(): void {
    isCollapsed.value = !isCollapsed.value
  }

  // Reset method to clear all state
  function reset(): void {
    tables.value = []
    loading.value = false
    searchTerm.value = ''
    showInactive.value = false
    filterDiningAreaId.value = undefined
    filterStatus.value = undefined
  }

  return {
    // State
    tables,
    loading,
    searchTerm,
    showInactive,
    filterDiningAreaId,
    filterStatus,
    isCollapsed,

    // Computed
    filteredTables,
    totalTables,
    activeTables,
    inactiveTables,
    availableTables,
    occupiedTables,
    tableOptions,

    // Core methods
    fetchTables,
    fetchTableById,
    createTable,
    updateTable,
    updateTableStatus,
    deleteTable,
    activateTable,
    deactivateTableWithConfirm,
    deleteTableWithConfirm,

    // Utility methods
    getTableById,
    getTableByNumber,

    // Filter methods
    setSearchTerm,
    toggleShowInactive,
    setDiningAreaFilter,
    setStatusFilter,
    clearFilters,
    toggleCollapsed,

    // Reset method
    reset,
  }
}, {
  persist: {
    key: 'tables',
    storage: localStorage,
    paths: ['tables', 'isCollapsed'],
  },
})

export const createTablesStore = async (businessIdParam: string) => {
  const tablesStore = useTablesStore()
  const businessStore = useBusinessStore()

  businessStore.setBusinessId(businessIdParam)
  await tablesStore.fetchTables()

  return tablesStore
}
